import { useEffect, useRef, useState } from 'react';

import { isUcpImageUrl } from './useAuthenticatedImageSrc';

// Debounce window: multiple 404s arriving within this period are batched into a single state update.
const DEBOUNCE_MS = 200;

// Max time to wait for errors before considering validation complete.
// CDN 404s respond in <500ms; this covers that plus debounce margin.
const VALIDATION_DEADLINE_MS = 700;

type UseValidatedImageLinksResult = {
  validLinks: string[];
  allBroken: boolean;
  validating: boolean;
};

const useValidatedImageLinks = (links: string[] | undefined): UseValidatedImageLinksResult => {
  const [state, setState] = useState<UseValidatedImageLinksResult>(() => ({
    validLinks: links ?? [],
    allBroken: false,
    validating: (links?.length ?? 0) > 0,
  }));

  // needed for correct array key
  const linksKey = links?.join('\0') ?? '';
  const linksRef = useRef(links);
  linksRef.current = links;

  useEffect(() => {
    const currentLinks = linksRef.current;

    if (!currentLinks || currentLinks.length === 0) {
      setState({ validLinks: [], allBroken: false, validating: false });
      return undefined;
    }

    const hasNonUcp = currentLinks.some((url) => !isUcpImageUrl(url));
    if (!hasNonUcp) {
      // All UCP — nothing to validate
      setState({ validLinks: currentLinks, allBroken: false, validating: false });
      return undefined;
    }

    setState({ validLinks: currentLinks, allBroken: false, validating: true });

    const broken = new Set<string>();
    let cancelled = false;
    let debounceTimer: number | undefined;
    let deadlineTimer: number | undefined;

    const applyFilter = () => {
      if (cancelled) return;
      clearTimeout(debounceTimer);
      clearTimeout(deadlineTimer);
      const filtered = currentLinks.filter((url) => !broken.has(url));

      setState({
        validLinks: filtered,
        allBroken: filtered.length === 0,
        validating: false,
      });
    };

    // Fallback: if no errors arrive within the deadline, all images are assumed valid
    deadlineTimer = window.setTimeout(() => {
      if (cancelled) return;

      if (broken.size > 0) {
        applyFilter();
      } else {
        setState((prev) => ({ ...prev, validating: false }));
      }
    }, VALIDATION_DEADLINE_MS);

    currentLinks.forEach((url) => {
      if (isUcpImageUrl(url)) return;
      const img = new Image();
      img.onerror = () => {
        if (cancelled) return;
        broken.add(url);

        clearTimeout(debounceTimer);
        debounceTimer = window.setTimeout(applyFilter, DEBOUNCE_MS);
      };
      img.src = url;
    });

    return () => {
      // cleanup
      cancelled = true;
      clearTimeout(debounceTimer);
      clearTimeout(deadlineTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linksKey]);

  return state;
};

export default useValidatedImageLinks;
